import { useState, forwardRef } from 'react'
import type { ReactNode } from 'react'
import isError from 'lodash/isError'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@klear/ui/components/alert-dialog'

type DeleteButtonWrapperProps = {
  className?: string
  onSelect?: (e: Event) => void
  children?: ReactNode
}

const DefaultWrapper = forwardRef<HTMLDivElement, DeleteButtonWrapperProps>(({ className, children }, ref) => (
  <div ref={ref} className={className}>
    {children}
  </div>
))
DefaultWrapper.displayName = 'DefaultWrapper'

interface DeleteButtonProps {
  wrapperComponent?: React.ComponentType<DeleteButtonWrapperProps>
  children?: ReactNode
  resourceId: number
  resourceName: string
  resourceType: string
  deleteMutation: (params: { id: number }) => Promise<unknown>
  isDeleting?: boolean
  onSuccess?: () => void
  customOnClick?: () => void
}

export function DeleteButton({
  resourceId,
  resourceName,
  resourceType,
  deleteMutation,
  isDeleting = false,
  onSuccess,
  children,
  customOnClick,
  wrapperComponent: WrapperComponent = DefaultWrapper
}: DeleteButtonProps) {
  const [open, setOpen] = useState(false)

  const handleDelete = async () => {
    if (customOnClick) {
      customOnClick()
      return
    }

    try {
      await deleteMutation({ id: resourceId })
      onSuccess?.()
    } catch (error) {
      console.error('Error deleting', isError(error) ? error.message : error)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <WrapperComponent
          className="text-destructive focus:text-destructive cursor-pointer"
          onSelect={(e: Event) => e?.preventDefault()}
        >
          {children || 'Delete'}
        </WrapperComponent>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {resourceType}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {resourceName}? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
